import Model, { attr } from '@ember-data/model';
import { service } from '@ember/service';

import UAParser from 'ua-parser-js';

import { apiBaseURL } from 'receivable-invoices-js/constants/hosts';

import { PAYMENT_FREQUENCIES } from '../constants/sepa-direct-debits';

class InvalidError extends Error {
  constructor(errors) {
    super('Invalid Error');
    this.name = 'InvalidError';
    this.errors = errors;
  }
}

export default class MandateModel extends Model {
  @service deviceManager;

  @attr('string') mandateId;

  //mandate data
  @attr('string') mandateReferenceCode;
  @attr('date') mandateSignatureDate;

  //SDD data
  @attr('date') sddCollectionDate;
  @attr sddAmount;
  @attr('boolean', { defaultValue: false }) sddIsOverdue;
  @attr('string') sddScheduleType;
  @attr('number') sddScheduleCount;
  @attr('string') sddReference;
  @attr('string') sddCustomScheduleUnit;
  @attr('string') sddCustomScheduleInterval;

  //client data
  @attr('string') clientName;
  @attr('string') clientEmail;
  @attr('string') clientLocale;
  @attr('string') clientAddress;
  @attr('string') clientCity;
  @attr('string') clientZipCode;
  @attr('string') clientProvinceCode;
  @attr('string') clientCountryCode;
  @attr('string') clientIban;
  @attr('boolean', { defaultValue: false }) addressWasPrefilled;

  //organization data
  @attr('string') organizationName;
  @attr('string') organizationCreditorIdentifier;
  @attr('string') organizationLogoUrl;
  @attr('string') organizationAddress;

  get isSddRecurring() {
    return this.sddScheduleType !== PAYMENT_FREQUENCIES.ONE_OFF;
  }

  trackingProperties(shouldIncludeDuration) {
    let device = new UAParser().getDevice().type;

    let properties = {
      collection_type: this.isSddRecurring ? 'recurring' : 'one-off',
      platform: device === 'mobile' ? 'mobile' : 'web',
      address_prefilled: this.addressWasPrefilled,
    };

    if (this.isSddRecurring) {
      properties.frequency = this.sddScheduleType;
      if (shouldIncludeDuration) {
        properties.duration = this.sddScheduleCount === 0 ? 'no_limit' : 'number_payments';
      }
    }

    return properties;
  }

  async fetchMandate(mandateId) {
    let response = await fetch(
      `${apiBaseURL}/v1/direct_debit_collection_mandates/shared/${mandateId}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      if (response.status === 412 || response.status === 404 || response.status === 400) {
        let error = await response.json();
        let errorMessage = error.errors?.[0]?.code;
        let errorToThrow =
          errorMessage === 'payment_link_expired' ? 'mandate_expired' : 'mandate_invalid';
        throw new Error(errorToThrow);
      }
    }

    let payload = await response.json();

    this.mandateId = payload.data.id;
    this.organizationName = payload.data.attributes.organization_snapshot.name;
    this.organizationLogoUrl = payload.data.attributes.organization_snapshot.logo_url;
    this.sddScheduleType = payload.data.attributes.payment_info.schedule_type;
    this.sddScheduleCount = payload.data.attributes.payment_info.schedule_total_collection_count;
    this.sddCustomScheduleUnit = payload.data.attributes.payment_info.schedule_custom_unit;
    this.sddCustomScheduleInterval = payload.data.attributes.payment_info.interval;
    this.sddAmount = payload.data.attributes.payment_info.first_payment.amount;
    this.sddCollectionDate = payload.data.attributes.payment_info.first_payment.collection_date;
    this.sddReference = payload.data.attributes.payment_info.first_payment.reference;
    this.sddIsOverdue = payload.data.attributes.payment_info.first_payment.is_overdue;

    this.clientName = this.buildClientName(payload.data.attributes.client_snapshot);
    this.clientEmail = payload.data.attributes.client_snapshot.email;
    this.clientAddress = payload.data.attributes.client_snapshot.address;
    this.clientZipCode = payload.data.attributes.client_snapshot.zip_code;
    this.clientCity = payload.data.attributes.client_snapshot.city;
    this.clientCountryCode = payload.data.attributes.client_snapshot.country_code;

    this.organizationCreditorIdentifier = payload.data.attributes.creditor_identifier;

    let addressLine1 = payload.data.attributes.organization_snapshot.address_line_1;
    let addressLine2 = payload.data.attributes.organization_snapshot.address_line_2;
    let address = `${addressLine1}${addressLine2 ? ` ${addressLine2}` : ''}`;
    let organizationZipCode = payload.data.attributes.organization_snapshot.zip_code;
    let organizationCity = payload.data.attributes.organization_snapshot.city;
    let organizationCountry = payload.data.attributes.organization_snapshot.country;

    this.organizationAddress = `${address}, ${organizationZipCode} ${organizationCity}, ${organizationCountry}`;
    this.sddReference = payload.data.attributes.payment_info.first_payment.reference;
    this.mandateReferenceCode = payload.data.attributes.unique_mandate_reference;

    this.clientLocale = payload.data.attributes.client_snapshot.locale;

    this.addressWasPrefilled = Boolean(
      this.clientAddress ||
        this.clientZipCode ||
        this.clientCity ||
        this.clientCountryCode ||
        this.clientProvinceCode
    );

    return this;
  }

  buildClientName(clientSnapshot) {
    let { name, first_name, last_name } = clientSnapshot;

    if (name) return name;
    return `${first_name || ''} ${last_name || ''}`.trim();
  }

  buildDataForSignMandate() {
    let clientSnapshot = {
      ...(this.clientAddress ? { address: this.clientAddress } : {}),
      ...(this.clientCity ? { city: this.clientCity } : {}),
      ...(this.clientZipCode ? { zip_code: this.clientZipCode } : {}),
      ...(this.clientProvinceCode ? { province_code: this.clientProvinceCode } : {}),
      ...(this.clientCountryCode ? { country_code: this.clientCountryCode } : {}),
    };

    return {
      data: {
        type: 'direct-debit-collection-mandates',
        attributes: {
          client_iban: this.clientIban,
          client_snapshot: clientSnapshot,
        },
      },
    };
  }

  async signMandate() {
    // reset errors
    this.errors.remove('clientAddress', 'required');
    this.errors.remove('clientZipCode', 'required');
    this.errors.remove('clientCity', 'required');
    this.errors.remove('clientCountryCode', 'required');
    this.errors.remove('clientIban', 'required');
    this.errors.remove('clientProvinceCode', 'required');

    let data = this.buildDataForSignMandate();

    try {
      let headers = {};
      headers['X-Qonto-Device'] = this.deviceManager.id;
      let response = await fetch(
        `${apiBaseURL}/v1/direct_debit_collection_mandates/shared/${this.mandateId}/sign`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
          headers,
        }
      );

      if (!response.ok) {
        let payload = await response.json();

        if (response.status === 422) {
          throw new InvalidError(payload.errors);
        } else {
          throw payload;
        }
      }

      // do something with payload or route to success page
      return true;
    } catch (error) {
      if (this.isInvalidErrorWithPointers(error) && this.isKnownError(error)) {
        error.errors.forEach(e => this.addError(e.source.pointer, e.code));
      } else {
        throw error;
      }
    }
  }

  isInvalidErrorWithPointers(error) {
    return error instanceof InvalidError && error.errors;
  }

  isKnownError(error) {
    let possibleErrors = [
      'mandate_not_found',
      'mandate_already_signed',
      'required',
      'len',
      'invalid_iban',
      'sdd_disabled',
    ];

    return error.errors.some(e => possibleErrors.includes(e.code));
  }

  addError(pointer, code) {
    let fieldPointer = pointer.split('/').pop();
    let pointerToModelField = {
      client_iban: 'clientIban',
      zip_code: 'clientZipCode',
    };
    let modelField = pointerToModelField[fieldPointer];
    this.errors.add(modelField, code);
  }

  addErrors() {
    if (!this.clientAddress) {
      this.errors.add('clientAddress', 'required');
    }

    if (!this.clientZipCode) {
      this.errors.add('clientZipCode', 'required');
    }

    if (!this.clientCity) {
      this.errors.add('clientCity', 'required');
    }

    if (!this.clientCountryCode) {
      this.errors.add('clientCountryCode', 'required');
    }

    if (!this.clientIban) {
      this.errors.add('clientIban', 'required');
    }

    if (this.clientCountryCode === 'IT' && !this.clientProvinceCode) {
      this.errors.add('clientProvinceCode', 'required');
    }

    return this.errors.length > 0;
  }
}
